// ============================================================
// PaymentSuccess.jsx  –  Fresh Hills QR Payment Success
// Mobile-first, matches Fresh Hills brand UI
// ============================================================
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  QrCodeIcon, HomeIcon, ShareIcon,
  ShieldCheckIcon, ClockIcon, SparklesIcon,
} from '@heroicons/react/24/outline';

const PaymentSuccess = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { orderNumber, amount } = location.state || {};
  const [show, setShow] = useState(false);

  /* entrance animation trigger */
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 80);
    return () => clearTimeout(t);
  }, []);

  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    .ps-page {
      min-height:100vh;
      background:linear-gradient(150deg,#f0fdf4 0%,#ffffff 55%,#f0f9ff 100%);
      display:flex; flex-direction:column; align-items:center;
      justify-content:center; padding:1.25rem 1rem;
      position:relative; overflow:hidden;
    }

    /* Blobs */
    .ps-blob {
      position:absolute; border-radius:50%; filter:blur(72px); pointer-events:none; z-index:0;
    }
    .ps-blob-1 { width:22rem; height:22rem; background:#a7f3d0; opacity:0.18; top:-5rem; left:-5rem; }
    .ps-blob-2 { width:18rem; height:18rem; background:#bae6fd; opacity:0.14; bottom:-4rem; right:-4rem; }

    /* Card */
    .ps-card {
      position:relative; z-index:2; width:100%; max-width:420px;
      background:white; border-radius:2rem;
      box-shadow:0 24px 60px -12px rgba(0,0,0,0.12);
      border:1px solid #f3f4f6; overflow:hidden;
      opacity:0; transform:translateY(32px) scale(0.97);
      transition:opacity 0.5s ease, transform 0.5s cubic-bezier(0.175,0.885,0.32,1.275);
    }
    .ps-card.show { opacity:1; transform:translateY(0) scale(1); }

    /* Gradient top banner */
    .ps-banner {
      background:linear-gradient(135deg,#059669,#0891b2);
      padding:2rem 1.5rem 1.5rem;
      text-align:center; position:relative; overflow:hidden;
    }
    .ps-banner::before {
      content:''; position:absolute; inset:0;
      background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }

    /* Logo row */
    .ps-logo { display:flex; align-items:center; justify-content:center; gap:0.5rem; margin-bottom:1.5rem; }
    .ps-logo img { height:32px; width:auto; object-fit:contain; }
    .ps-logo span {
      font-size:1rem; font-weight:900; color:white;
    }

    /* Check icon */
    .ps-check-wrap {
      width:72px; height:72px; border-radius:50%;
      background:rgba(255,255,255,0.25); border:3px solid rgba(255,255,255,0.5);
      display:flex; align-items:center; justify-content:center;
      margin:0 auto 1rem;
      animation: ps-pop 0.6s 0.3s cubic-bezier(0.175,0.885,0.32,1.275) both;
    }
    @media(min-width:400px){ .ps-check-wrap{ width:84px; height:84px; } }
    .ps-check-wrap svg { width:40px; height:40px; color:white; }
    @media(min-width:400px){ .ps-check-wrap svg{ width:48px; height:48px; } }

    @keyframes ps-pop {
      from { transform:scale(0); opacity:0; }
      to   { transform:scale(1); opacity:1; }
    }

    .ps-banner-title {
      font-size:1.4rem; font-weight:900; color:white; margin-bottom:0.3rem;
    }
    @media(min-width:400px){ .ps-banner-title{ font-size:1.6rem; } }
    .ps-banner-sub { font-size:0.85rem; color:rgba(255,255,255,0.88); font-weight:600; }

    /* Body */
    .ps-body { padding:1.5rem; }
    @media(min-width:400px){ .ps-body{ padding:1.75rem; } }

    /* Receipt box */
    .ps-receipt {
      background:#f8fafc; border-radius:1.25rem;
      border:1.5px dashed #e2e8f0; padding:1.1rem 1.25rem;
      margin-bottom:1.25rem;
    }
    .ps-receipt-row {
      display:flex; align-items:center; justify-content:space-between;
      padding:0.45rem 0; border-bottom:1px solid #f1f5f9;
    }
    .ps-receipt-row:last-child { border-bottom:none; }
    .ps-receipt-label { font-size:0.8rem; font-weight:700; color:#64748b; display:flex; align-items:center; gap:0.35rem; }
    .ps-receipt-label svg { width:0.8rem; height:0.8rem; }
    .ps-receipt-value { font-size:0.88rem; font-weight:800; color:#111827; }
    .ps-receipt-value.green {
      font-size:1.2rem;
      background:linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    }

    /* Instruction pill */
    .ps-instruction {
      display:flex; align-items:center; gap:0.75rem;
      background:#ecfdf5; border:1px solid #a7f3d0;
      border-radius:1rem; padding:0.85rem 1rem; margin-bottom:1.25rem;
    }
    .ps-instruction-icon {
      width:36px; height:36px; border-radius:0.65rem; flex-shrink:0;
      background:linear-gradient(135deg,#10b981,#059669);
      display:flex; align-items:center; justify-content:center;
    }
    .ps-instruction-icon svg { width:1rem; height:1rem; color:white; }
    .ps-instruction-text { font-size:0.82rem; font-weight:700; color:#065f46; line-height:1.4; }

    /* Buttons */
    .ps-btn-primary {
      width:100%; padding:0.9rem; border-radius:9999px; border:none; cursor:pointer;
      background:linear-gradient(135deg,#10b981,#059669); color:white;
      font-weight:900; font-size:0.95rem; font-family:'Nunito',sans-serif;
      display:flex; align-items:center; justify-content:center; gap:0.5rem;
      box-shadow:0 4px 14px rgba(16,185,129,0.3); transition:all 0.3s;
      margin-bottom:0.6rem;
    }
    .ps-btn-primary svg { width:1rem; height:1rem; }
    .ps-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(16,185,129,0.35); }

    .ps-btn-outline {
      width:100%; padding:0.85rem; border-radius:9999px; cursor:pointer;
      background:white; color:#374151; border:1.5px solid #e5e7eb;
      font-weight:700; font-size:0.9rem; font-family:'Nunito',sans-serif;
      display:flex; align-items:center; justify-content:center; gap:0.5rem;
      transition:all 0.2s;
    }
    .ps-btn-outline svg { width:1rem; height:1rem; }
    .ps-btn-outline:hover { border-color:#059669; color:#059669; background:#f0fdf4; }

    /* Trust row */
    .ps-trust {
      display:flex; align-items:center; justify-content:center; gap:1.25rem;
      margin-top:1.25rem; flex-wrap:wrap;
    }
    .ps-trust-item {
      display:flex; align-items:center; gap:0.3rem;
      font-size:0.7rem; font-weight:700; color:#94a3b8;
    }
    .ps-trust-item svg { width:0.8rem; height:0.8rem; color:#10b981; }

    /* Confetti dots */
    .ps-confetti { position:fixed; inset:0; pointer-events:none; z-index:1; overflow:hidden; }
    .ps-dot {
      position:absolute; width:8px; height:8px; border-radius:50%;
      animation:ps-fall linear forwards;
    }
    @keyframes ps-fall {
      0%   { transform:translateY(-20px) rotate(0deg); opacity:1; }
      100% { transform:translateY(100vh) rotate(720deg); opacity:0; }
    }
  `;

  const dots = Array.from({ length: 16 }, (_, i) => ({
    left: `${5 + i * 6}%`,
    delay: `${(i * 0.15).toFixed(2)}s`,
    dur:   `${1.5 + (i % 4) * 0.3}s`,
    color: ['#10b981','#0891b2','#f59e0b','#7c3aed','#059669'][i % 5],
    size:  `${6 + (i % 3) * 3}px`,
  }));

  return (
    <>
      <style>{styles}</style>

      {/* Confetti */}
      <div className="ps-confetti">
        {dots.map((d, i) => (
          <div key={i} className="ps-dot" style={{
            left: d.left, top: '-20px',
            width: d.size, height: d.size,
            background: d.color,
            animationDelay: d.delay,
            animationDuration: d.dur,
          }} />
        ))}
      </div>

      <div className="ps-page">
        <div className="ps-blob ps-blob-1" />
        <div className="ps-blob ps-blob-2" />

        <div className={`ps-card ${show ? 'show' : ''}`}>

          {/* Green gradient banner */}
          <div className="ps-banner">
            <div className="ps-logo">
              <img src="/images/logo.png" alt="Fresh Hills"
                onError={e => { e.target.onerror=null; e.target.style.display='none'; }} />
              <span>Fresh Hills</span>
            </div>
            <div className="ps-check-wrap">
              <CheckCircleIcon />
            </div>
            <div className="ps-banner-title">Payment Successful! 🎉</div>
            <div className="ps-banner-sub">Your order is confirmed</div>
          </div>

          {/* Body */}
          <div className="ps-body">

            {/* Receipt */}
            <div className="ps-receipt">
              <div className="ps-receipt-row">
                <span className="ps-receipt-label">
                  <SparklesIcon /> Order No.
                </span>
                <span className="ps-receipt-value">{orderNumber || '—'}</span>
              </div>
              <div className="ps-receipt-row">
                <span className="ps-receipt-label">
                  <ClockIcon /> Date &amp; Time
                </span>
                <span className="ps-receipt-value">{date}, {time}</span>
              </div>
              <div className="ps-receipt-row">
                <span className="ps-receipt-label">
                  <ShieldCheckIcon /> Amount Paid
                </span>
                <span className="ps-receipt-value green">₹{amount}</span>
              </div>
            </div>

            {/* Instruction */}
            <div className="ps-instruction">
              <div className="ps-instruction-icon"><QrCodeIcon /></div>
              <div className="ps-instruction-text">
                You may now open the fridge and collect your products. Please close the door properly after.
              </div>
            </div>

            {/* Actions */}
            <button className="ps-btn-primary" onClick={() => navigate('/scan')}>
              <QrCodeIcon /> Scan Another Fridge
            </button>
            <button className="ps-btn-outline" onClick={() => navigate('/')}>
              <HomeIcon /> Back to Home
            </button>

            {/* Trust row */}
            <div className="ps-trust">
              <div className="ps-trust-item"><ShieldCheckIcon /> Secure</div>
              <div className="ps-trust-item"><SparklesIcon /> Instant</div>
              <div className="ps-trust-item"><ClockIcon /> 24/7</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;